'use client';

/**
 * components/checkout/shared/hooks.ts
 *
 * Checkout behaviour shared by CheckoutModal and the /checkout/membership page.
 * Extracted from CheckoutModal.tsx on 2026-09-14 (brief: "extract the modal's
 * mint/confirm and return-restore logic into shared hooks — do not copy it").
 *
 *  useStripeAvailability  — Stripe.js failed to load / no key → one boolean.
 *  useStripeTestOverlayStrip — removes Stripe's test-mode "Developer Tools" easel.
 *  createReturnStash / useReturnRestore — off-site payment bounce (PayPal,
 *      Klarna, Revolut Pay, 3DS redirects): stash before confirm, restore from
 *      URL + sessionStorage on the return leg, read Stripe's redirect_status.
 *  usePaymentSubmission — the confirm lifecycle: one attempt at a time,
 *      stash-before-confirm, error → plain sentence, success → done.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { stripePromise } from './stripe-client';

/** True when Stripe.js cannot be used on this page (no key, or load rejected). */
export function useStripeAvailability(): boolean {
  const [stripeLoadError, setStripeLoadError] = useState(false);
  // 2026-09-04 Fix: detect if Stripe promise rejected (network failure, CSP, etc.)
  useEffect(() => {
    if (!stripePromise) return;
    stripePromise
      .then((stripe) => {
        if (!stripe) setStripeLoadError(true);
      })
      .catch(() => {
        setStripeLoadError(true);
      });
  }, []);
  return !stripePromise || stripeLoadError;
}

/**
 * Stripe injects a TEST-MODE "Developer Tools" overlay — the `elements-inner-easel`
 * iframe, a floating "stripe ›" tab pinned bottom-right (z-index 99999) — whenever
 * Elements mount with a pk_test_ key. It is NOT a functional payment surface and does
 * NOT appear in production (live key). Its inline `display:block !important` beats any
 * stylesheet rule, so CSS can't hide it — we remove the node itself.
 */
export function useStripeTestOverlayStrip(): void {
  useEffect(() => {
    const strip = () =>
      document
        .querySelectorAll('iframe[src*="elements-inner-easel"]')
        .forEach((el) => el.remove());
    strip();
    const observer = new MutationObserver(strip);
    observer.observe(document.body, { childList: true });
    return () => {
      observer.disconnect();
      strip();
    };
  }, []);
}

/**
 * sessionStorage rather than localStorage: one purchase attempt in one tab. It
 * must not outlive the tab or leak into a second one.
 */
export interface ReturnStash<T> {
  stash(state: T): void;
  read(): T | null;
  clear(): void;
}

export function createReturnStash<T extends object>(
  key: string,
  isValid: (parsed: Partial<T>) => boolean,
): ReturnStash<T> {
  return {
    stash(state) {
      try {
        sessionStorage.setItem(key, JSON.stringify(state));
      } catch {
        /* private browsing — the bounce will land on a fresh form, not a crash */
      }
    },
    read() {
      try {
        const raw = sessionStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<T>;
        return isValid(parsed) ? (parsed as T) : null;
      } catch {
        return null;
      }
    },
    clear() {
      try {
        sessionStorage.removeItem(key);
      } catch {
        /* ignore */
      }
    },
  };
}

/** Stripe's verdict on a redirect return: succeeded / pending → the buyer has paid. */
export type ReturnVerdict = 'paid' | 'failed' | 'none';
export function verdictFromRedirectStatus(status: string | null): ReturnVerdict {
  if (status === 'succeeded' || status === 'pending') return 'paid';
  if (status === 'failed' || status === 'canceled' || status === 'requires_payment_method') return 'failed';
  return 'none';
}

/**
 * Runs ONCE on mount. When `isReturnLeg(params)` matches the current URL and a
 * stash exists, hands both to `onRestore`. Stripe appends redirect_status to the
 * return_url; without reading it, a buyer coming back from PayPal or Klarna was
 * asked a second time for money already taken — so the verdict is passed along
 * and a paid verdict clears the stash here.
 */
export function useReturnRestore<T>({
  store,
  isReturnLeg,
  onRestore,
}: {
  store: ReturnStash<T>;
  isReturnLeg: (params: URLSearchParams) => boolean;
  onRestore: (stash: T, verdict: ReturnVerdict, params: URLSearchParams) => void;
}): void {
  const restored = useRef(false);
  const onRestoreRef = useRef(onRestore);
  onRestoreRef.current = onRestore;
  useEffect(() => {
    if (restored.current) return;
    restored.current = true;
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (!isReturnLeg(params)) return;
    const stash = store.read();
    if (!stash) return;
    const verdict = verdictFromRedirectStatus(params.get('redirect_status'));
    if (verdict === 'paid') store.clear();
    onRestoreRef.current(stash, verdict, params);
    // Mount-only by design (the ref guard makes a re-run a no-op anyway).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/**
 * The confirm lifecycle. `run(attempt)` refuses re-entry, clears the last error,
 * calls `beforeAttempt` (stash for a possible bounce), awaits the attempt, and
 * maps its outcome: 'done' → success state, `{ error }` → the sentence shown,
 * 'pending' → nothing (the attempt set its own UI, e.g. a swap panel).
 */
export type AttemptOutcome = 'done' | 'pending' | { error: string };

export function usePaymentSubmission(initialDone = false) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(initialDone);
  const inFlight = useRef(false);

  const run = useCallback(
    async (attempt: () => Promise<AttemptOutcome>, beforeAttempt?: () => void) => {
      if (inFlight.current) return;
      inFlight.current = true;
      setError(null);
      setSubmitting(true);
      beforeAttempt?.();
      try {
        const outcome = await attempt();
        if (outcome === 'done') setDone(true);
        else if (outcome !== 'pending') setError(outcome.error);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      } finally {
        inFlight.current = false;
        setSubmitting(false);
      }
    },
    [],
  );

  return { submitting, error, setError, done, setDone, run };
}
